import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useRuntimeHealthAccess } from "@/hooks/useRuntimeHealthAccess";
import { Plus, RefreshCw, Send, Trash2, Users } from "lucide-react";


type Recipient = {
  id: string;
  channel: "email" | "slack";
  destination: string;
  label: string | null;
  route_pattern: string | null;
  per_route_threshold_override: number | null;
  min_severity: "warn" | "error";
  enabled: boolean;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  last_test_error: string | null;
};

type Draft = {
  channel: "email" | "slack";
  destination: string;
  label: string;
  route_pattern: string;
  per_route_threshold_override: string;
  min_severity: "warn" | "error";
};

const EMPTY_DRAFT: Draft = {
  channel: "email",
  destination: "",
  label: "",
  route_pattern: "",
  per_route_threshold_override: "",
  min_severity: "warn",
};

export function FrontendErrorRecipientsPanel() {
  const { toast } = useToast();
  const { canConfigure } = useRuntimeHealthAccess();
  const [rows, setRows] = useState<Recipient[]>([]);


  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("frontend_error_alert_recipients")
      .select("*")
      .order("channel", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) {
      toast({ title: "Could not load recipients", description: error.message, variant: "destructive" });
    } else {
      setRows((data ?? []) as unknown as Recipient[]);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    const destination = draft.destination.trim();
    if (!destination) {
      toast({ title: "Destination required", variant: "destructive" });
      return;
    }
    if (draft.channel === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(destination)) {
      toast({ title: "Enter a valid email address", variant: "destructive" });
      return;
    }
    if (draft.channel === "slack" && !/^https:\/\/hooks\.slack\.com\//.test(destination)) {
      toast({
        title: "Enter a Slack incoming webhook URL",
        description: "It should start with https://hooks.slack.com/",
        variant: "destructive",
      });
      return;
    }
    setAdding(true);
    const override = Number(draft.per_route_threshold_override);
    const { error } = await supabase.from("frontend_error_alert_recipients").insert({
      channel: draft.channel,
      destination,
      label: draft.label.trim() || null,
      route_pattern: draft.route_pattern.trim() || null,
      per_route_threshold_override: Number.isFinite(override) && override >= 1 ? Math.round(override) : null,
      min_severity: draft.min_severity,
    });
    setAdding(false);
    if (error) {
      toast({ title: "Could not add recipient", description: error.message, variant: "destructive" });
      return;
    }
    setDraft(EMPTY_DRAFT);
    toast({ title: "Recipient added" });
    void load();
  };

  const patch = async (id: string, changes: Partial<Recipient>) => {
    setBusyId(id);
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...changes } : r)));
    const { error } = await supabase
      .from("frontend_error_alert_recipients")
      .update(changes as never)
      .eq("id", id);
    setBusyId(null);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      void load();
    }
  };

  const remove = async (id: string) => {
    setBusyId(id);
    const { error } = await supabase.from("frontend_error_alert_recipients").delete().eq("id", id);
    setBusyId(null);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const testSend = async (recipient: Recipient) => {
    setBusyId(recipient.id);
    const { data, error } = await supabase.functions.invoke("frontend-error-alerts-monitor", {
      body: { mode: "test_recipient", recipient_id: recipient.id },
    });
    setBusyId(null);
    const res = data as { ok?: boolean; delivered?: boolean; error?: string } | null;
    if (error || !res?.delivered) {
      toast({
        title: "Test send failed",
        description: res?.error ?? error?.message ?? "Unknown error",
        variant: "destructive",
      });
    } else {
      toast({ title: `Test ${recipient.channel} sent`, description: recipient.destination });
    }
    void load();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="size-4 text-primary" /> Alert recipients &amp; channels
          </CardTitle>
          <CardDescription>
            Fan alerts out to several email addresses and Slack channels. A recipient can be scoped to
            one page (or a page prefix) and can carry its own per-page threshold, so noisy pages only
            page the team that owns them.
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {!canConfigure && (
          <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            Read-only: admin role required to add, edit, test, or remove alert recipients.
          </p>
        )}
        <div
          className={`grid gap-3 rounded-md border border-border p-3 sm:grid-cols-2 lg:grid-cols-6 ${
            canConfigure ? "" : "hidden"
          }`}
        >

          <div className="space-y-1.5">
            <Label htmlFor="rec-channel" className="text-xs">Channel</Label>
            <Select
              value={draft.channel}
              onValueChange={(v) => setDraft((d) => ({ ...d, channel: v as Draft["channel"] }))}
            >
              <SelectTrigger id="rec-channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="slack">Slack webhook</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 lg:col-span-2">
            <Label htmlFor="rec-dest" className="text-xs">
              {draft.channel === "email" ? "Email address" : "Slack webhook URL"}
            </Label>
            <Input
              id="rec-dest"
              value={draft.destination}
              placeholder={
                draft.channel === "email" ? "oncall@yourdomain.com" : "https://hooks.slack.com/services/..."
              }
              onChange={(e) => setDraft((d) => ({ ...d, destination: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-label" className="text-xs">Label</Label>
            <Input
              id="rec-label"
              value={draft.label}
              placeholder="#eng-alerts"
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-route" className="text-xs">Page override</Label>
            <Input
              id="rec-route"
              value={draft.route_pattern}
              placeholder="/dashboard (blank = all)"
              onChange={(e) => setDraft((d) => ({ ...d, route_pattern: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rec-threshold" className="text-xs">Threshold override</Label>
            <Input
              id="rec-threshold"
              type="number"
              min={1}
              value={draft.per_route_threshold_override}
              placeholder="default"
              onChange={(e) => setDraft((d) => ({ ...d, per_route_threshold_override: e.target.value }))}
            />
          </div>
          <div className="flex items-end lg:col-span-6">
            <Button onClick={() => void add()} disabled={adding}>
              <Plus className="size-4" /> {adding ? "Adding…" : "Add recipient"}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {rows.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              No recipients yet — alerts fall back to the single email/webhook in Error alerts above.
            </p>
          )}
          {rows.map((r) => (
            <div key={r.id} className="space-y-2 rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={r.channel === "slack" ? "secondary" : "outline"}>{r.channel}</Badge>
                <span className="font-mono text-xs break-all">{r.destination}</span>
                {r.label && <span className="text-xs text-muted-foreground">{r.label}</span>}
                <div className="ml-auto flex items-center gap-2">
                  <Label htmlFor={`rec-on-${r.id}`} className="text-xs text-muted-foreground">
                    Enabled
                  </Label>
                  <Switch
                    id={`rec-on-${r.id}`}
                    checked={r.enabled}
                    disabled={!canConfigure || busyId === r.id}
                    onCheckedChange={(v) => void patch(r.id, { enabled: v })}
                  />
                  {canConfigure && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === r.id}
                        onClick={() => void testSend(r)}
                      >
                        <Send className="size-3.5" /> Test send
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Remove ${r.destination}`}
                        disabled={busyId === r.id}
                        onClick={() => void remove(r.id)}
                      >
                        <Trash2 className="size-3.5 text-destructive" />
                      </Button>
                    </>
                  )}

                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor={`rec-route-${r.id}`} className="text-xs">
                    Page override (blank = all pages)
                  </Label>
                  <Input
                    id={`rec-route-${r.id}`}
                    disabled={!canConfigure}
                    defaultValue={r.route_pattern ?? ""}
                    placeholder="/dashboard"
                    onBlur={(e) => {
                      const value = e.target.value.trim() || null;
                      if (value !== r.route_pattern) void patch(r.id, { route_pattern: value });
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`rec-th-${r.id}`} className="text-xs">Threshold override</Label>
                  <Input
                    id={`rec-th-${r.id}`}
                    disabled={!canConfigure}
                    type="number"
                    min={1}
                    defaultValue={r.per_route_threshold_override ?? ""}
                    placeholder="use global"
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      const value = Number.isFinite(n) && n >= 1 ? Math.round(n) : null;
                      if (value !== r.per_route_threshold_override) {
                        void patch(r.id, { per_route_threshold_override: value });
                      }
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`rec-sev-${r.id}`} className="text-xs">Minimum severity</Label>
                  <Select
                    disabled={!canConfigure}
                    value={r.min_severity}
                    onValueChange={(v) => void patch(r.id, { min_severity: v as Recipient["min_severity"] })}
                  >
                    <SelectTrigger id={`rec-sev-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="warn">Warn and above</SelectItem>
                      <SelectItem value="error">Errors only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {r.last_test_at && (
                <p className="text-xs text-muted-foreground">
                  Last test {new Date(r.last_test_at).toLocaleString()} —{" "}
                  {r.last_test_ok ? (
                    <span className="text-primary">delivered</span>
                  ) : (
                    <span className="text-destructive">failed: {r.last_test_error ?? "unknown"}</span>
                  )}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default FrontendErrorRecipientsPanel;
